import { Box, Button, Stack, Tab, Tabs, type Theme, Typography } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { NamedLayoutBlock } from 'src/components';
import {
  SetUserPasswordParams,
  useGroupRules,
  usePlatformUser,
  UserGroupRules,
} from 'src/graphql';
import { usePartnerDomain, usePartnerDomains } from 'src/graphql/entities/partner-domain';
import { useSearchPlatformUsers } from 'src/graphql/entities/search/search-platform-user';
import { DEFAULT_PAGINATION_LIMIT } from 'src/graphql/entities/search/use-search';
import { useManageUserNotificationPreferences } from 'src/graphql/entities/user-communication-preferences';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { PlatformUserRaw } from 'src/types';
import { useCopyToClipboard } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { AssigneeGroupsView } from './assignee-groups/assignee-groups-view';
import { UsersActionsMenu } from './users-actions-menu';
import { UsersPartnerDomainTable } from './users-partner-domain-table';
import { UsersSearchInput } from './users-search-input';
import { UsersTable } from './users-table';

const tabSx = {
  color: (theme: Theme) => theme.palette.custom.OV.sys.onSurface,
  '&.Mui-selected': {
    color: (theme: Theme) => theme.palette.custom.OV.sys.primary,
  },
} as const;

export const Users = () => {
  const { navigate, pathnameWithoutPrefix, partnerPrefix } = useNavigateRoute();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { checkValidity } = useGroupRules();
  const canManageAssigneeGroups = checkValidity([UserGroupRules.AccessToManageAssigneeGroups]);
  const isAssigneeGroupsTab =
    pathnameWithoutPrefix === portalPaths.settings.usersAssigneeGroups();
  const { partnerDomains } = usePartnerDomains(selectedPartnerId);
  const { createPartnerDomain, deletePartnerDomain } = usePartnerDomain(selectedPartnerId);
  const {
    updatePlatformUser,
    createPlatformUser,
    updatePlatformUserActiveState,
    setUserPassword,
  } = usePlatformUser({
    partnerId: selectedPartnerId,
  });

  const { manageUserCommunicationPreferences } = useManageUserNotificationPreferences();

  const { showModal } = useModalMethods(Modals.AddPartnerDomainModal);

  const [searchUserTerm, setSearchUserTerm] = useState<string>('');
  const [userSorting, setUserSorting] = useState<{ id: string; desc: boolean } | null>({
    id: 'email',
    desc: false,
  });

  const userTableFilter = useMemo(() => {
    return [
      {
        attribute: 'isActive',
        operator: '=',
        value: pathnameWithoutPrefix === portalPaths.settings.users() ? 'true' : 'false',
        valueType: 'boolean',
      },
    ];
  }, [pathnameWithoutPrefix]);

  const {
    records: platformUsers,
    loadMore,
    hasMore,
    isLoading,
    variables,
  } = useSearchPlatformUsers({
    searchTerm: searchUserTerm ? searchUserTerm : null,
    partnerId: selectedPartnerId,
    shouldIncludePlatformPartnerId: false,
    showResultsWhenEmptySearchTerm: true,
    limit: DEFAULT_PAGINATION_LIMIT,
    sort: userSorting ? { attribute: userSorting.id, desc: userSorting.desc } : undefined,
    filter: userTableFilter,
  });

  const { copyToClipboard } = useCopyToClipboard();

  const onAddDomain = useCallback(
    (domain: string) => {
      void createPartnerDomain({ domain });
    },
    [createPartnerDomain],
  );

  const onUserUpdate = useCallback(
    async (data: Partial<PlatformUserRaw>) => {
      const updated = await updatePlatformUser(data, variables);
      if (!updated) {
        throw new Error('Failed to update platform user');
      }
      await manageUserCommunicationPreferences({
        userId: data.id!,
        userCommunicationPreferences: data.userCommunicationPreferences || [],
        userGroups: data.userGroups || [],
      });
      return updated;
    },
    [updatePlatformUser, variables, manageUserCommunicationPreferences],
  );

  const onRemoveDomain = useCallback(
    (domainId: string) => {
      void deletePartnerDomain(domainId);
    },
    [deletePartnerDomain],
  );

  const onCopyUserId = useCallback(
    async (userId: string) => {
      await copyToClipboard(userId);
    },
    [copyToClipboard],
  );

  const onCreateUser = useCallback(
    async (data: Partial<PlatformUserRaw>) => {
      const userId = await createPlatformUser(data, variables);
      if (!userId) {
        throw new Error('Failed to create platform user');
      }
      return userId;
    },
    [createPlatformUser, variables],
  );

  const onUpdateUserActiveState = useCallback(
    (data: Partial<PlatformUserRaw>) => {
      void updatePlatformUserActiveState(data, variables);
    },
    [updatePlatformUserActiveState, variables],
  );

  const onSetUserPassword = useCallback(
    (data: SetUserPasswordParams) => {
      void setUserPassword(data);
    },
    [setUserPassword],
  );

  const onSortChange = useCallback(
    (params: { id: string; desc: boolean }) => {
      if (!params) {
        setUserSorting(null);
        return;
      }

      setUserSorting(params);
    },
    [setUserSorting],
  );

  const existingDomainsNames = useMemo(
    () => partnerDomains.map((item) => item.domain),
    [partnerDomains],
  );

  if (isAssigneeGroupsTab && !canManageAssigneeGroups) {
    const target = partnerPrefix
      ? `/${partnerPrefix}${portalPaths.settings.users()}`
      : portalPaths.settings.users();
    return <Navigate to={target} replace />;
  }

  return (
    <NamedLayoutBlock title="Users" sx={{ flex: 1, display: { sm: 'flex' } }}>
      <Stack direction="column" spacing={2.5} sx={{ px: 2, py: 2, flex: 1, minHeight: 0 }}>
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Allowed Domains
          </Typography>
          <Stack direction="row" alignItems="flex-start" gap={2}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Anyone with an email address at these domains can sign up.
            </Typography>
            <Button
              size="small"
              onClick={() => {
                showModal({
                  existingDomains: existingDomainsNames,
                  onSubmit: onAddDomain,
                });
              }}
              variant="contained"
            >
              Add domain
            </Button>
          </Stack>
          <UsersPartnerDomainTable onDelete={onRemoveDomain} domains={partnerDomains} />
        </Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="overline" color="text.secondary">
            {isAssigneeGroupsTab ? 'Manage Assignee Groups' : 'Manage Users'}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            aria-hidden={isAssigneeGroupsTab || undefined}
            sx={{
              visibility: isAssigneeGroupsTab ? 'hidden' : 'visible',
              pointerEvents: isAssigneeGroupsTab ? 'none' : 'auto',
            }}
          >
            <UsersSearchInput onChange={setSearchUserTerm} value={searchUserTerm} />
            <UsersActionsMenu onCreateUser={onCreateUser} partnerId={selectedPartnerId} />
          </Stack>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tabs
            aria-label="Tab navigation"
            value={pathnameWithoutPrefix}
            variant="standard"
            TabIndicatorProps={{
              sx: {
                backgroundColor: (theme) => theme.palette.custom.OV.sys.primary,
              },
            }}
          >
            <Tab
              label="Active"
              onClick={() => navigate(portalPaths.settings.users())}
              value={portalPaths.settings.users()}
              sx={tabSx}
            />
            <Tab
              label="Inactive"
              onClick={() => navigate(portalPaths.settings.usersDisabled())}
              value={portalPaths.settings.usersDisabled()}
              sx={tabSx}
            />
            {canManageAssigneeGroups ? (
              <Tab
                label="Assignee Groups"
                data-testid="settings-users-assignee-groups-tab"
                onClick={() => navigate(portalPaths.settings.usersAssigneeGroups())}
                value={portalPaths.settings.usersAssigneeGroups()}
                sx={tabSx}
              />
            ) : null}
          </Tabs>
        </Box>

        {isAssigneeGroupsTab ? (
          <AssigneeGroupsView partnerId={selectedPartnerId} />
        ) : (
          <Box sx={{ flex: 1, minHeight: 700 }}>
            <UsersTable
              key={pathnameWithoutPrefix}
              onUpdateUser={onUserUpdate}
              onUpdateUserActiveState={onUpdateUserActiveState}
              onCopyId={onCopyUserId}
              users={platformUsers}
              onSetUserPassword={onSetUserPassword}
              sort={userSorting}
              onSortChange={onSortChange}
              loadMore={loadMore}
              hasMore={hasMore}
              isLoading={isLoading}
            />
          </Box>
        )}
      </Stack>
    </NamedLayoutBlock>
  );
};
